import { useState, useMemo, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { articles } from "@/mocks/articles";

const ARTICLES_PER_PAGE = 9;

// ── Like count helpers (synced with ArticleDetail) ────────────
const LIKES_KEY = "articleLikes";
const LIKED_ARTICLES_KEY = "likedArticles";

const getStoredLikes = (): Record<string, number> => {
  try { return JSON.parse(localStorage.getItem(LIKES_KEY) || "{}"); } catch { return {}; }
};
const getLikedArticles = (): string[] => {
  try { return JSON.parse(localStorage.getItem(LIKED_ARTICLES_KEY) || "[]"); } catch { return []; }
};

const baseLikeCounts: Record<string, number> = {
  "kapadokya-rehberi": 284,
  "pamukkale-efes-rehberi": 197,
  "turkiye-underrated-yerler": 342,
  "gaziantep-gastronomi": 218,
  "nemrut-gobeklitepe-rehberi": 156,
  "karadeniz-yaylalari": 203,
  "istanbul-gizli-noktalar": 389,
  "ege-adaları-rehberi": 174,
  "istanbul-gezilecek-yerler": 451,
  "sakarya-gezilecek-yerler": 128,
  "antalya-gezilecek-yerler": 267,
  "izmir-gezilecek-yerler": 231,
  "bursa-gezilecek-yerler": 189,
  "ankara-gezilecek-yerler": 142,
  "trabzon-gezilecek-yerler": 176,
  "konya-gezilecek-yerler": 153,
  "gaziantep-gezilecek-yerler": 198,
  "mardin-gezilecek-yerler": 167,
  "kapadokya-gezilecek-yerler": 221,
};

const getTotalLikeCount = (articleId: string): number => {
  const base = baseLikeCounts[articleId] || 0;
  const stored = getStoredLikes()[articleId] || 0;
  return base + stored;
};

const READING_LIST_KEY = "turkiye_reading_list";

const getReadingList = (): string[] => {
  try {
    return JSON.parse(localStorage.getItem(READING_LIST_KEY) || "[]");
  } catch {
    return [];
  }
};

const saveReadingList = (ids: string[]) => {
  localStorage.setItem(READING_LIST_KEY, JSON.stringify(ids));
};

const categories = ["Tümü", "Gezi Rehberi", "Keşif", "Gastronomi", "Doğa", "Tarih & Arkeoloji", "Şehir Rehberi", "Ada & Deniz"];

const regions = ["Tümü", "Marmara", "Ege", "Akdeniz", "İç Anadolu", "Karadeniz", "Doğu Anadolu", "Güneydoğu Anadolu", "Trakya"];

const articleRegionMap: Record<string, string[]> = {
  "kapadokya-rehberi": ["İç Anadolu"],
  "pamukkale-efes-rehberi": ["Ege"],
  "turkiye-underrated-yerler": ["Marmara", "Ege", "Akdeniz", "İç Anadolu", "Karadeniz", "Doğu Anadolu", "Güneydoğu Anadolu"],
  "gaziantep-gastronomi": ["Güneydoğu Anadolu"],
  "nemrut-gobeklitepe-rehberi": ["Güneydoğu Anadolu"],
  "karadeniz-yaylalari": ["Karadeniz"],
  "istanbul-gizli-noktalar": ["Marmara"],
  "ege-adaları-rehberi": ["Ege", "Marmara"],
  "istanbul-gezilecek-yerler": ["Marmara"],
  "sakarya-gezilecek-yerler": ["Marmara"],
  "antalya-gezilecek-yerler": ["Akdeniz"],
  "izmir-gezilecek-yerler": ["Ege"],
  "ankara-gezilecek-yerler": ["İç Anadolu"],
  "trabzon-gezilecek-yerler": ["Karadeniz"],
  "konya-gezilecek-yerler": ["İç Anadolu"],
  "gaziantep-gezilecek-yerler": ["Güneydoğu Anadolu"],
  "mardin-gezilecek-yerler": ["Güneydoğu Anadolu"],
  "kapadokya-gezilecek-yerler": ["İç Anadolu"],
  "bursa-gezilecek-yerler": ["Marmara"],
};

const regionColors: Record<string, string> = {
  "Marmara":           "bg-violet-100 text-violet-700",
  "Ege":               "bg-cyan-100 text-cyan-700",
  "Akdeniz":           "bg-rose-100 text-rose-700",
  "İç Anadolu":        "bg-amber-100 text-amber-700",
  "Karadeniz":         "bg-emerald-100 text-emerald-700",
  "Doğu Anadolu":      "bg-orange-100 text-orange-700",
  "Güneydoğu Anadolu": "bg-red-100 text-red-700",
  "Trakya":            "bg-indigo-100 text-indigo-700",
};

const readTimeFilters = [
  { label: "Tümü", min: 0, max: 999 },
  { label: "5 dk altı", min: 0, max: 5 },
  { label: "5–10 dk", min: 5, max: 10 },
  { label: "10–20 dk", min: 10, max: 20 },
  { label: "20 dk üzeri", min: 20, max: 999 },
];

const categoryColors: Record<string, string> = {
  "Gezi Rehberi":      "bg-amber-100 text-amber-700",
  "Keşif":             "bg-emerald-100 text-emerald-700",
  "Gastronomi":        "bg-rose-100 text-rose-700",
  "Doğa":              "bg-sky-100 text-sky-700",
  "Tarih & Arkeoloji": "bg-orange-100 text-orange-700",
  "Şehir Rehberi":     "bg-violet-100 text-violet-700",
  "Ada & Deniz":       "bg-cyan-100 text-cyan-700",
};

const parseReadTime = (rt: string) => parseInt(rt.replace(/\D/g, ""), 10) || 0;

const ArticlesPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedCat, setSelectedCat] = useState("Tümü");
  const [selectedRegion, setSelectedRegion] = useState("Tümü");
  const [selectedReadTime, setSelectedReadTime] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [readingList, setReadingList] = useState<string[]>(getReadingList);
  const [showReadingList, setShowReadingList] = useState(false);
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const [likedArticles, setLikedArticles] = useState<string[]>(getLikedArticles);

  // Current page from URL ?page=N
  const currentPage = Math.max(1, parseInt(searchParams.get("page") || "1", 10));

  const setPage = useCallback((page: number) => {
    const params = new URLSearchParams(searchParams);
    if (page === 1) {
      params.delete("page");
    } else {
      params.set("page", String(page));
    }
    setSearchParams(params, { replace: false });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [searchParams, setSearchParams]);

  // Re-sync liked articles when returning from detail page
  useEffect(() => {
    const syncLikes = () => setLikedArticles(getLikedArticles());
    window.addEventListener("focus", syncLikes);
    return () => window.removeEventListener("focus", syncLikes);
  }, []);

  const toggleReadingList = useCallback((id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setReadingList((prev) => {
      const isIn = prev.includes(id);
      const next = isIn ? prev.filter((x) => x !== id) : [...prev, id];
      saveReadingList(next);
      setToastMsg(isIn ? "Okuma listesinden çıkarıldı" : "Okuma listesine eklendi");
      setTimeout(() => setToastMsg(null), 2200);
      return next;
    });
  }, []);

  const readingListArticles = useMemo(
    () => articles.filter((a) => readingList.includes(a.id)),
    [readingList]
  );

  useEffect(() => {
    const pageLabel = safePage > 1 ? ` — Sayfa ${safePage}` : "";
    const title = `Türkiye Gezi Makaleleri ve Şehir Rehberleri — Seyahat Yazıları${pageLabel}`;
    const desc = "Kapadokya, İstanbul, Gaziantep, Trabzon ve daha fazlası için kapsamlı gezi rehberleri. Deneyimli editörlerimizin kaleme aldığı seyahat yazıları ve keşif makaleleri.";
    const canonicalBase = "https://geziturkiyem.com/articles";
    const canonicalUrl = safePage > 1 ? `${canonicalBase}?page=${safePage}` : canonicalBase;

    document.title = title;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    const setCanonical = (url: string) => {
      let el = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!el) { el = document.createElement("link"); el.setAttribute("rel", "canonical"); document.head.appendChild(el); }
      el.setAttribute("href", url);
    };
    const setLinkRel = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
      if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
      el.setAttribute("href", href);
    };
    const removeLinkRel = (rel: string) => {
      document.querySelector(`link[rel="${rel}"]`)?.remove();
    };
    const injectSchema = (id: string, data: object) => {
      let el = document.getElementById(id);
      if (!el) { el = document.createElement("script"); el.id = id; el.setAttribute("type", "application/ld+json"); document.head.appendChild(el); }
      el.textContent = JSON.stringify(data);
    };

    setCanonical(canonicalUrl);

    // Prev / Next link tags for pagination SEO
    if (safePage > 1) {
      const prevUrl = safePage === 2 ? canonicalBase : `${canonicalBase}?page=${safePage - 1}`;
      setLinkRel("prev", prevUrl);
    } else {
      removeLinkRel("prev");
    }
    if (safePage < totalPages) {
      setLinkRel("next", `${canonicalBase}?page=${safePage + 1}`);
    } else {
      removeLinkRel("next");
    }

    setMeta("description", desc);
    setMeta("keywords", "türkiye gezi rehberi, seyahat makaleleri, şehir rehberleri, kapadokya rehberi, istanbul gezi, karadeniz yaylaları, gaziantep gastronomi");
    setMeta("og:title", title, true);
    setMeta("og:description", desc, true);
    setMeta("og:type", "website", true);
    setMeta("og:url", canonicalUrl, true);
    setMeta("og:site_name", "Yerel Bilgi — Türkiye Seyahat Rehberi", true);
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:site", "@geziturkiyem");
    setMeta("twitter:title", title);
    setMeta("twitter:description", desc);

    injectSchema("schema-articles-page", {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": title,
      "description": desc,
      "url": canonicalUrl,
      "inLanguage": "tr-TR",
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Ana Sayfa", "item": "https://geziturkiyem.com/" },
          { "@type": "ListItem", "position": 2, "name": "Makaleler & Rehberler", "item": canonicalBase },
          ...(safePage > 1 ? [{ "@type": "ListItem", "position": 3, "name": `Sayfa ${safePage}`, "item": canonicalUrl }] : []),
        ],
      },
    });

    return () => { document.title = "Türkiye Seyahat Rehberi"; };
  }, [safePage, totalPages]);

  const filtered = useMemo(() => {
    const rtFilter = readTimeFilters[selectedReadTime];
    return articles.filter((a) => {
      const matchCat = selectedCat === "Tümü" || a.category === selectedCat;
      const matchSearch =
        !searchQuery ||
        a.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        a.shortDesc.toLowerCase().includes(searchQuery.toLowerCase());
      const mins = parseReadTime(a.readTime);
      const matchTime = mins >= rtFilter.min && mins < rtFilter.max;
      const articleRegions = articleRegionMap[a.id] || [];
      const matchRegion = selectedRegion === "Tümü" || articleRegions.includes(selectedRegion);
      return matchCat && matchSearch && matchTime && matchRegion;
    });
  }, [selectedCat, selectedRegion, selectedReadTime, searchQuery]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCat, selectedRegion, selectedReadTime, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / ARTICLES_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedArticles = filtered.slice(
    (safePage - 1) * ARTICLES_PER_PAGE,
    safePage * ARTICLES_PER_PAGE
  );

  const featured = articles.filter((a) => a.featured);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Toast Notification */}
      {toastMsg && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-stone-900 text-white text-sm px-5 py-3 rounded-full flex items-center gap-2">
          <i className="ri-bookmark-fill text-amber-400"></i>
          {toastMsg}
        </div>
      )}

      {/* Reading List Drawer */}
      {showReadingList && (
        <div className="fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowReadingList(false)} />
          <div className="relative ml-auto w-full max-w-md bg-white h-full flex flex-col">
            <div className="flex items-center justify-between px-6 py-5 border-b border-stone-100">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center bg-amber-100 rounded-xl">
                  <i className="ri-bookmark-fill text-amber-600 text-base"></i>
                </div>
                <div>
                  <h3 className="font-bold text-stone-900 text-base" style={{ fontFamily: "'Playfair Display', serif" }}>
                    Okuma Listem
                  </h3>
                  <p className="text-xs text-stone-400">{readingListArticles.length} makale kaydedildi</p>
                </div>
              </div>
              <button
                onClick={() => setShowReadingList(false)}
                className="w-8 h-8 flex items-center justify-center text-stone-400 hover:text-stone-700 cursor-pointer rounded-full hover:bg-stone-100 transition-colors"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              {readingListArticles.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center py-16">
                  <div className="w-16 h-16 flex items-center justify-center bg-stone-100 rounded-full mb-4">
                    <i className="ri-bookmark-line text-stone-400 text-2xl"></i>
                  </div>
                  <p className="text-stone-500 text-sm font-medium">Henüz makale eklemediniz</p>
                  <p className="text-stone-400 text-xs mt-1">Makale kartlarındaki yer imi ikonuna tıklayın</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {readingListArticles.map((a) => (
                    <div
                      key={a.id}
                      className="flex items-start gap-3 p-3 rounded-xl border border-stone-100 hover:border-amber-200 hover:bg-amber-50/50 transition-all group"
                    >
                      <div
                        className="w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden cursor-pointer"
                        onClick={() => { setShowReadingList(false); navigate(`/article/${a.id}`); }}
                      >
                        <img src={a.image} alt={a.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p
                          className="text-stone-900 text-sm font-semibold line-clamp-2 leading-snug cursor-pointer hover:text-amber-700 transition-colors"
                          style={{ fontFamily: "'Playfair Display', serif" }}
                          onClick={() => { setShowReadingList(false); navigate(`/article/${a.id}`); }}
                        >
                          {a.title}
                        </p>
                        <div className="flex items-center gap-2 mt-1.5">
                          <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${categoryColors[a.category] || "bg-stone-100 text-stone-600"}`}>
                            {a.category}
                          </span>
                          <span className="text-xs text-stone-400 flex items-center gap-1">
                            <i className="ri-time-line"></i>{a.readTime}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={(e) => toggleReadingList(a.id, e)}
                        className="w-7 h-7 flex items-center justify-center text-amber-500 hover:text-rose-500 cursor-pointer flex-shrink-0 transition-colors"
                        title="Listeden çıkar"
                      >
                        <i className="ri-bookmark-fill text-base"></i>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {readingListArticles.length > 0 && (
              <div className="px-6 py-4 border-t border-stone-100">
                <button
                  onClick={() => { setReadingList([]); saveReadingList([]); }}
                  className="w-full text-sm text-stone-400 hover:text-rose-500 transition-colors cursor-pointer py-2"
                >
                  Listeyi Temizle
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Header */}
      <section className="relative pt-32 pb-16 bg-stone-900 overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Turkey%20travel%20writing%20journal%20articles%20guide%20books%20map%20compass%20adventure%20exploration&width=1400&height=400&seq=art-header&orientation=landscape"
          alt="Makaleler"
          className="absolute inset-0 w-full h-full object-cover object-top opacity-30"
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 text-center">
          <span className="text-amber-400 text-sm font-semibold uppercase tracking-widest">Rehberler</span>
          <h1
            className="text-4xl md:text-5xl font-bold text-white mt-2 mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Makaleler & Rehberler
          </h1>
          <p className="text-stone-300 text-base max-w-xl mx-auto">
            Deneyimli editörlerimizin kaleme aldığı kapsamlı gezi rehberleri ve keşif yazıları.
          </p>
          <button
            onClick={() => setShowReadingList(true)}
            className="mt-6 inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white text-sm px-5 py-2.5 rounded-full transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-bookmark-line"></i>
            Okuma Listem
            {readingList.length > 0 && (
              <span className="bg-amber-500 text-white text-xs font-bold w-5 h-5 flex items-center justify-center rounded-full">
                {readingList.length}
              </span>
            )}
          </button>
        </div>
      </section>

      {/* Popular Tags */}
      <section className="bg-stone-50 border-b border-stone-100 py-5">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-xs font-semibold text-stone-500 uppercase tracking-wider whitespace-nowrap">Popüler Etiketler:</span>
            {(() => {
              const tagCount: Record<string, number> = {};
              articles.forEach((a) => a.tags.forEach((t) => { tagCount[t] = (tagCount[t] || 0) + 1; }));
              const sorted = Object.entries(tagCount).sort((a, b) => b[1] - a[1]).slice(0, 18);
              return sorted.map(([tag, count]) => (
                <button
                  key={tag}
                  onClick={() => setSearchQuery(tag)}
                  className={`flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border font-medium transition-all cursor-pointer whitespace-nowrap
                    ${searchQuery === tag
                      ? "bg-amber-500 text-white border-amber-500"
                      : "bg-white text-stone-600 border-stone-200 hover:border-amber-300 hover:text-amber-700"
                    }`}
                >
                  <span className="text-stone-400 text-xs">#</span>
                  {tag}
                  <span className={`text-xs ml-0.5 ${searchQuery === tag ? "text-amber-100" : "text-stone-400"}`}>
                    {count}
                  </span>
                </button>
              ));
            })()}
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-stone-200 text-stone-600 hover:bg-stone-300 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-close-line"></i> Temizle
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Featured Strip */}
      {featured.length > 0 && (
        <section className="bg-amber-50 border-b border-amber-100 py-6">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-fire-fill text-amber-500"></i>
              </div>
              <span className="text-sm font-bold text-amber-700 uppercase tracking-wider">Öne Çıkan Makaleler</span>
            </div>
            <div className="flex gap-4 overflow-x-auto pb-2">
              {featured.map((a) => (
                <button
                  key={a.id}
                  onClick={() => navigate(`/article/${a.id}`)}
                  className="flex-shrink-0 flex items-center gap-3 bg-white border border-amber-100 rounded-xl px-4 py-3 cursor-pointer hover:border-amber-300 hover:bg-amber-50 transition-all group w-72"
                >
                  <div className="w-14 h-14 flex-shrink-0 rounded-lg overflow-hidden">
                    <img src={a.image} alt={a.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300" />
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <div className="flex items-center gap-1 mb-1">
                      <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${categoryColors[a.category] || "bg-stone-100 text-stone-600"}`}>
                        {a.category}
                      </span>
                    </div>
                    <p className="text-stone-900 text-xs font-semibold line-clamp-2 leading-snug" style={{ fontFamily: "'Playfair Display', serif" }}>
                      {a.title}
                    </p>
                    <div className="flex items-center gap-1 mt-1 text-stone-400 text-xs">
                      <i className="ri-time-line"></i>
                      <span>{a.readTime}</span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Filters */}
      <section className="sticky top-16 z-30 bg-white border-b border-stone-100 py-4">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
            <div className="flex items-center bg-stone-50 border border-stone-200 rounded-full px-4 py-2 gap-2 flex-1 max-w-xs">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-search-line text-stone-400 text-sm"></i>
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Makale ara..."
                className="bg-transparent text-sm text-stone-700 outline-none flex-1"
              />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {categories.map((c) => (
                <button
                  key={c}
                  onClick={() => setSelectedCat(c)}
                  className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors cursor-pointer whitespace-nowrap
                    ${selectedCat === c ? "bg-amber-500 text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"}`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap mt-3">
            <div className="flex items-center gap-1 text-stone-400 text-xs mr-1">
              <i className="ri-map-2-line"></i>
              <span>Bölge:</span>
            </div>
            {regions.map((r) => (
              <button
                key={r}
                onClick={() => setSelectedRegion(r)}
                className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors cursor-pointer whitespace-nowrap
                  ${selectedRegion === r
                    ? "bg-stone-800 text-white"
                    : r !== "Tümü"
                      ? `${regionColors[r] || "bg-stone-100 text-stone-600"} hover:opacity-80`
                      : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                  }`}
              >
                {r}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 flex-wrap mt-3">
            <div className="flex items-center gap-1 text-stone-400 text-xs mr-1">
              <i className="ri-time-line"></i>
              <span>Okuma Süresi:</span>
            </div>
            {readTimeFilters.map((rt, i) => (
              <button
                key={rt.label}
                onClick={() => setSelectedReadTime(i)}
                className={`text-xs px-3 py-1 rounded-full border transition-colors cursor-pointer whitespace-nowrap
                  ${selectedReadTime === i ? "border-amber-500 text-amber-600 bg-amber-50" : "border-stone-200 text-stone-500 hover:border-stone-300"}`}
              >
                {rt.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
            <p className="text-sm text-stone-400">
              {filtered.length} makale bulundu
              {totalPages > 1 && (
                <span className="ml-2 text-stone-300">
                  — Sayfa {safePage} / {totalPages}
                </span>
              )}
            </p>
            {totalPages > 1 && (
              <div className="flex items-center gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`w-8 h-8 flex items-center justify-center rounded-full text-xs font-semibold transition-colors cursor-pointer ${
                      p === safePage
                        ? "bg-amber-500 text-white"
                        : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            )}
          </div>

          {filtered.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <i className="ri-article-line text-stone-300 text-4xl"></i>
              </div>
              <p className="text-stone-400">Arama kriterlerinize uygun makale bulunamadı.</p>
            </div>
          ) : (
            <div className="divide-y divide-stone-100">
              {paginatedArticles.map((article) => {
                const isSaved = readingList.includes(article.id);
                return (
                  <div key={article.id} className="group flex items-center gap-5 py-4 hover:bg-stone-50 px-2 transition-colors">
                    <Link to={`/article/${article.id}`} className="flex items-center gap-5 flex-1 min-w-0">
                      <div className="relative w-20 h-20 flex-shrink-0 overflow-hidden rounded-lg">
                        <img
                          src={article.image}
                          alt={article.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className={`text-xs px-2 py-0.5 rounded font-medium ${categoryColors[article.category] || "bg-stone-100 text-stone-600"}`}>
                            {article.category}
                          </span>
                          <span className="text-xs text-stone-400 flex items-center gap-1">
                            <i className="ri-time-line"></i>{article.readTime}
                          </span>
                          {article.featured && (
                            <span className="text-xs text-amber-600 font-medium">Öne Çıkan</span>
                          )}
                        </div>
                        <h3 className="font-bold text-stone-900 text-base group-hover:text-amber-700 transition-colors line-clamp-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                          {article.title}
                        </h3>
                        <p className="text-stone-500 text-xs leading-relaxed line-clamp-1 mt-0.5">{article.shortDesc}</p>
                        <div className="flex items-center gap-3 mt-1.5">
                          <span className="text-xs text-stone-400">{article.author}</span>
                          <span className={`flex items-center gap-1 text-xs font-medium ${likedArticles.includes(article.id) ? "text-rose-500" : "text-stone-400"}`}>
                            <i className={likedArticles.includes(article.id) ? "ri-heart-fill" : "ri-heart-line"}></i>
                            {getTotalLikeCount(article.id).toLocaleString("tr-TR")}
                          </span>
                        </div>
                      </div>
                    </Link>
                    <button
                      onClick={(e) => toggleReadingList(article.id, e)}
                      className={`w-8 h-8 flex items-center justify-center flex-shrink-0 transition-colors cursor-pointer ${isSaved ? "text-amber-500" : "text-stone-300 hover:text-amber-400"}`}
                    >
                      <i className={isSaved ? "ri-bookmark-fill" : "ri-bookmark-line"}></i>
                    </button>
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-stone-300 group-hover:text-amber-500 transition-colors">
                      <i className="ri-arrow-right-s-line"></i>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Bottom Pagination */}
          {totalPages > 1 && (
            <div className="mt-12 flex items-center justify-center gap-2 flex-wrap">
              {/* Prev */}
              <button
                onClick={() => setPage(safePage - 1)}
                disabled={safePage === 1}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                  safePage === 1
                    ? "bg-stone-100 text-stone-300 cursor-not-allowed"
                    : "bg-stone-100 text-stone-600 hover:bg-stone-200 cursor-pointer"
                }`}
              >
                <i className="ri-arrow-left-s-line"></i>
                Önceki
              </button>

              {/* Page numbers */}
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => {
                const isActive = p === safePage;
                const isNear = Math.abs(p - safePage) <= 2 || p === 1 || p === totalPages;
                if (!isNear) {
                  if (p === safePage - 3 || p === safePage + 3) {
                    return (
                      <span key={p} className="text-stone-300 text-sm px-1">
                        …
                      </span>
                    );
                  }
                  return null;
                }
                return (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`w-9 h-9 flex items-center justify-center rounded-full text-sm font-semibold transition-colors cursor-pointer ${
                      isActive
                        ? "bg-amber-500 text-white"
                        : "bg-stone-100 text-stone-600 hover:bg-amber-100 hover:text-amber-700"
                    }`}
                  >
                    {p}
                  </button>
                );
              })}

              {/* Next */}
              <button
                onClick={() => setPage(safePage + 1)}
                disabled={safePage === totalPages}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                  safePage === totalPages
                    ? "bg-stone-100 text-stone-300 cursor-not-allowed"
                    : "bg-stone-100 text-stone-600 hover:bg-stone-200 cursor-pointer"
                }`}
              >
                Sonraki
                <i className="ri-arrow-right-s-line"></i>
              </button>
            </div>
          )}

          {/* SEO: page info for crawlers */}
          {totalPages > 1 && (
            <div className="mt-6 text-center">
              <p className="text-xs text-stone-300">
                Toplam {filtered.length} makale — {totalPages} sayfa
              </p>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ArticlesPage;
